import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { apiFetch, API_BASE } from "./utils/apiClient";
import { isBoxAccount } from "./utils/userProfile";
import {
  computeCanvasCosts,
  buildPriceRow,
  frameData,
  kasData,
  getCanvasPriceOptions,
  getFramedPriceOptions,
  filterPriceRowsBySize,
} from "./utils/boardPriceCalc";
import PartnerPriceCalculator from "./partner/PartnerPriceCalculator";

/* ======================================================
   📌 أنواع اللوحة - كانفاس هو الديفولت وله نفس معادلة الأسعار
   القديمة (جدول Origin/Edition/Prime). باقي الأنواع سعرها المبدئي
   طول × عرض (بالمتر) × سعر الوحدة، وياخذون قيمة الوحدة حسب النوع
====================================================== */
const BOARD_TYPE_LABELS = {
  canvas: "🎨 كانفاس",
  wallpaper: "🧻 ورق جدران",
  acrylic: "🔷 اكرليك",
  thermalFoam: "🔥 طباعة حراري على فوم مباشر",
  thermalSticker: "🏷️ طباعة حراري ستيكر",
};

// 💰 سعر الطباعة للمتر الواحد الافتراضي لكل نوع لوحة - القيم الحقيقية تُجلب
// من السيرفر (GET /board-pricing) عند فتح الواجهة، وهذي القيم تُستخدم فقط
// كافتراضي مبدئي قبل اكتمال الجلب أو لو فشل الاتصال بالسيرفر. الأدمن يعدّل
// هذي الأسعار ويضغط چيك بوكس "اعتمد" بكل نوع فينعكس السعر الجديد فوراً عند
// كل الموظفات (نفس مصدر الحقيقة المشترك، بدل حالة محلية بكل متصفح).
const DEFAULT_BOARD_PRICING = {
  canvas: 25000,
  wallpaperWithInstall: 68500,
  wallpaperWithoutInstall: 46500,
  acrylic: 35000,
  thermalFoam: 35000,
  thermalSticker: 35000,
};

/* ======================================================
   📋 مكون الجدول الفرعي (PriceTable)
====================================================== */
const PriceTable = ({ title, data, width, height }) => {
  const filtered = filterPriceRowsBySize(data, width, height);

  return (
    <Paper sx={{ p: 2, mb: 4, borderRadius: 3, direction: 'rtl' }}>
      <Typography variant="h6" fontWeight={900} mb={2} textAlign="right">{title}</Typography>
      {filtered.length === 0 ? (
        <Typography color="error" textAlign="center">❌ لا توجد قياسات مطابقة</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell align="center" sx={{ fontWeight: 900 }}>القياس</TableCell>
                <TableCell align="center" sx={{ fontWeight: 900 }}>Origin</TableCell>
                <TableCell align="center" sx={{ fontWeight: 900 }}>Edition</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((row, i) => (
                <TableRow key={i}>
                  <TableCell align="center">{row[0]}</TableCell>
                  <TableCell align="center">{row[1]}</TableCell>
                  <TableCell align="center">{row[2]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

/* ======================================================
   💰 حقل سعر الطباعة للمتر (أدمن فقط) + چيك بوكس "اعتمد" - تعديل الرقم
   لا يغيّر السعر المستخدم بالحساب فوراً، لازم تأكيد بالضغط على "اعتمد"
   حتى يصير هذا السعر هو المعتمد ويظهر عند كل الموظفات
====================================================== */
const PriceApproveField = ({ label, value, onChangeValue, approved, saving, onApprove }) => (
  <Box>
    <TextField
      fullWidth
      label={label}
      type="number"
      value={value}
      onChange={(e) => onChangeValue(e.target.value)}
    />
    <FormControlLabel
      sx={{ mt: 0.5 }}
      control={
        <Checkbox
          checked={approved}
          disabled={saving || approved}
          onChange={onApprove}
        />
      }
      label={saving ? "جارِ الاعتماد..." : "اعتمد (يُطبَّق فوراً عند كل الموظفات)"}
    />
  </Box>
);

/* ======================================================
   🏠 الواجهة الرئيسية المدمجة
====================================================== */
// partnerMode: نسخة مبسّطة لواجهة ارت تاتش (/partner-price) - طول وعرض فقط،
// كانفاس ثابت، بدون اختيار نوع اللوحة ولا تعديل الأسعار ولا "أقصى قياس"
export default function BoardCostCalculator({ partnerMode = false }) {
  const [boardTypeState, setBoardType] = useState("canvas");
  const boardType = partnerMode ? "canvas" : boardTypeState;
  const [wallpaperInstall, setWallpaperInstall] = useState(true); // true = مع تركيب (الديفولت)
  const [lengthCm, setLengthCm] = useState("");
  const [widthCm, setWidthCm] = useState("");

  // 💰 سعر الطباعة للمتر المعتمد فعلياً (مشترك بين الأدمن وكل الموظفات) +
  // نسخة "مسودة" يعدّلها الأدمن قبل الضغط على چيك بوكس "اعتمد" لكل نوع لوحة
  const [boardPricing, setBoardPricing] = useState(DEFAULT_BOARD_PRICING);
  const [pricingDrafts, setPricingDrafts] = useState(DEFAULT_BOARD_PRICING);
  const [savingPriceField, setSavingPriceField] = useState(null);

  const username = localStorage.getItem("username");
  const isAdmin = username === "admin";
  // عمود Prime مخفي عن الموظفات، ونفس الشي بنسخة ارت تاتش
  const isEmployee = partnerMode || isBoxAccount() || ["jumana@123", "sara@123", "maryam@123", "farah@123", "karina@123", "Baneen@123", "maysa@123"].includes(username);
  // تعديل/اعتماد سعر المتر يبقى بالواجهة الأصلية فقط
  const canEditPricing = isAdmin && !partnerMode;
  const enterDimsText = partnerMode ? "أدخل الطول والعرض لإظهار النتائج" : "ادخلي الطول والعرض لإظهار النتائج";

  // 🔄 يجلب سعر الطباعة للمتر الحالي (المعتمد من الأدمن) عند فتح الواجهة -
  // نفس القيمة تظهر عند الأدمن وكل الموظفات لأنها مخزّنة مركزياً بالسيرفر
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch(`${API_BASE}/board-pricing`);
        if (!res.ok) return;
        const data = await res.json();
        setBoardPricing(data);
        setPricingDrafts(data);
      } catch (err) {
        console.error("Failed to fetch board pricing:", err);
      }
    })();
  }, []);

  // ✅ يرسل السعر الجديد لهذا الحقل للسيرفر (زر/چيك بوكس "اعتمد") - بعد
  // النجاح يصير هذا السعر هو المعتمد فوراً لكل من يفتح الواجهة (أدمن أو موظفة)
  const handleApprovePrice = async (field) => {
    const value = Number(pricingDrafts[field]);
    if (!Number.isFinite(value) || value <= 0) return;

    setSavingPriceField(field);
    try {
      const res = await apiFetch(`${API_BASE}/board-pricing`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "فشل اعتماد السعر");
      }
      setBoardPricing((prev) => ({ ...prev, [field]: value }));
    } catch (err) {
      console.error("Error approving board price:", err);
      window.alert("حدث خطأ أثناء اعتماد السعر");
    } finally {
      setSavingPriceField(null);
    }
  };

  // الدوال المساعدة للحساب
  const toNumber = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const fmtIQD = (n) => `${Math.round(n).toLocaleString()} IQD`;

  const { results, isStandard } = useMemo(() => {
    const L = toNumber(lengthCm);
    const W = toNumber(widthCm);
    if (L <= 0 || W <= 0) return { results: null, isStandard: false };

    // فحص إذا كان القياس ستاندرد موجود في أي من القوائم
    const checkStandard = (data) => {
      return data.some(([size]) => {
        const [sw, sh] = size.replace(/\s/g, "").split("×").map(Number);
        return (sw === L && sh === W) || (sw === W && sh === L);
      });
    };

    const foundInAil = checkStandard(frameData);
    const foundInKas = checkStandard(kasData);
    const standardMatch = foundInAil || foundInKas;

    // الحسابات التلقائية - تعتمد على القيمة المكتوبة بالحقل حالاً (حتى قبل
    // الضغط على "اعتمد")، حتى ينعكس أي تغيير يدوي بالسعر فوراً على المبلغ
    const unitPrice = toNumber(pricingDrafts.canvas) || DEFAULT_BOARD_PRICING.canvas;
    const { printCost, innerWood, ail, kas } = computeCanvasCosts(L, W, unitPrice);
    const buildRow = buildPriceRow;

    // نسخة ارت تاتش: الكانفاس أول سطر (الافتراضي) + أيل + كاس - نفس دوال
    // فورم ارت تاتش بالضبط (القياس الستاندرد لأيل/كاس من الجدول المرجعي،
    // غيره بالمعادلة)، حتى الحاسبة والفورم يطلعون نفس الأرقام دائماً
    if (partnerMode) {
      const toRow = (title, options) => ({
        title,
        origin: options.find((o) => o.id === "origin")?.price ?? 0,
        edition: options.find((o) => o.id === "edition")?.price ?? 0,
      });
      return {
        isStandard: false,
        results: {
          debug: { printCost, innerWood, ail, kas },
          rows: [
            toRow("🎨 كانفاس (طباعة + إطار داخلي خشب)", getCanvasPriceOptions(L, W, unitPrice)),
            toRow(`🖼️ إطار أيل (L Frame)${standardMatch ? " – سعر ستاندرد" : ""}`, getFramedPriceOptions(L, W, unitPrice, "L")),
            toRow(`🖼️ إطار كاس (Kas Frame)${standardMatch ? " – سعر ستاندرد" : ""}`, getFramedPriceOptions(L, W, unitPrice, "Kas")),
          ],
        },
      };
    }

    return {
      isStandard: standardMatch,
      results: {
        debug: { printCost, innerWood, ail, kas },
        rows: [
          buildRow("لوحة طباعة+اطار داخلي خشب (طباعة فقط)", printCost + innerWood),
          buildRow("لوحة طباعة + اطار داخلي خشب واطار خارجي أيل", printCost + innerWood + ail),
          buildRow("لوحة طباعة + اطار داخلي خشب واطار خارجي كاس", printCost + innerWood + kas),
        ],
      }
    };
  }, [lengthCm, widthCm, pricingDrafts, partnerMode]);

  // 📦 سعر الأنواع غير الكانفاس: طول × عرض (بالمتر) × سعر الوحدة حسب النوع
  const simplePriceInfo = useMemo(() => {
    if (boardType === "canvas") return null;
    const L = toNumber(lengthCm);
    const W = toNumber(widthCm);
    if (L <= 0 || W <= 0) return null;

    const areaM2 = (L / 100) * (W / 100);
    // نفس منطق الكانفاس: يعتمد على القيمة المكتوبة بالحقل حالاً (قبل
    // الاعتماد)، حتى ينعكس أي تعديل يدوي فوراً على المبلغ المعروض
    const unitPrice =
      boardType === "wallpaper"
        ? (wallpaperInstall ? pricingDrafts.wallpaperWithInstall : pricingDrafts.wallpaperWithoutInstall)
        : pricingDrafts[boardType];

    return { areaM2, unitPrice, total: areaM2 * unitPrice };
  }, [boardType, wallpaperInstall, lengthCm, widthCm, pricingDrafts]);

  // نسخة ارت تاتش: نفس النتائج والجداول المحسوبة فوق، بتصميم واجهة الشريك
  if (partnerMode) {
    return (
      <PartnerPriceCalculator
        lengthCm={lengthCm}
        widthCm={widthCm}
        setLengthCm={setLengthCm}
        setWidthCm={setWidthCm}
        results={results}
        frameData={frameData}
        kasData={kasData}
      />
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6, direction: "rtl" }}>
      {/* 1️⃣ حاسبة التكلفة العلوية */}
      <Paper sx={{ p: 4, borderRadius: 4, mb: 4, boxShadow: 3 }}>
        <Typography variant="h5" fontWeight={900} mb={3} align="center">📏 حساب سعر اللوحة</Typography>
        
        <Grid container spacing={2} mb={3}>
          {!partnerMode && (
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="نوع اللوحة"
              value={boardType}
              onChange={(e) => setBoardType(e.target.value)}
            >
              {Object.entries(BOARD_TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          )}
          {boardType === "wallpaper" && (
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="التركيب"
                value={wallpaperInstall ? "with" : "without"}
                onChange={(e) => setWallpaperInstall(e.target.value === "with")}
              >
                <MenuItem value="with">مع تركيب</MenuItem>
                <MenuItem value="without">بدون تركيب</MenuItem>
              </TextField>
            </Grid>
          )}
          <Grid item xs={6}>
            <TextField fullWidth label="الطول (سم)" type="number" value={lengthCm} onChange={(e) => setLengthCm(e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="العرض (سم)" type="number" value={widthCm} onChange={(e) => setWidthCm(e.target.value)} />
          </Grid>
          {canEditPricing && boardType === "canvas" && (
            <Grid item xs={12}>
              <PriceApproveField
                label="سعر الطباعة للمتر (كانفاس)"
                value={pricingDrafts.canvas}
                onChangeValue={(v) => setPricingDrafts((prev) => ({ ...prev, canvas: v }))}
                approved={Number(pricingDrafts.canvas) === Number(boardPricing.canvas)}
                saving={savingPriceField === "canvas"}
                onApprove={() => handleApprovePrice("canvas")}
              />
            </Grid>
          )}
          {canEditPricing && boardType === "wallpaper" && (
            <>
              <Grid item xs={12} sm={6}>
                <PriceApproveField
                  label="سعر الطباعة للمتر (ورق جدران - بدون تركيب)"
                  value={pricingDrafts.wallpaperWithoutInstall}
                  onChangeValue={(v) => setPricingDrafts((prev) => ({ ...prev, wallpaperWithoutInstall: v }))}
                  approved={Number(pricingDrafts.wallpaperWithoutInstall) === Number(boardPricing.wallpaperWithoutInstall)}
                  saving={savingPriceField === "wallpaperWithoutInstall"}
                  onApprove={() => handleApprovePrice("wallpaperWithoutInstall")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <PriceApproveField
                  label="سعر الطباعة للمتر (ورق جدران - مع تركيب)"
                  value={pricingDrafts.wallpaperWithInstall}
                  onChangeValue={(v) => setPricingDrafts((prev) => ({ ...prev, wallpaperWithInstall: v }))}
                  approved={Number(pricingDrafts.wallpaperWithInstall) === Number(boardPricing.wallpaperWithInstall)}
                  saving={savingPriceField === "wallpaperWithInstall"}
                  onApprove={() => handleApprovePrice("wallpaperWithInstall")}
                />
              </Grid>
            </>
          )}
          {canEditPricing && ["acrylic", "thermalFoam", "thermalSticker"].includes(boardType) && (
            <Grid item xs={12}>
              <PriceApproveField
                label={`سعر الطباعة للمتر (${BOARD_TYPE_LABELS[boardType]})`}
                value={pricingDrafts[boardType]}
                onChangeValue={(v) => setPricingDrafts((prev) => ({ ...prev, [boardType]: v }))}
                approved={Number(pricingDrafts[boardType]) === Number(boardPricing[boardType])}
                saving={savingPriceField === boardType}
                onApprove={() => handleApprovePrice(boardType)}
              />
            </Grid>
          )}
        </Grid>

        {boardType !== "canvas" && (
          simplePriceInfo ? (
            <Box sx={{ p: 3, textAlign: "center", backgroundColor: "#e3f2fd", borderRadius: 2, border: "1px solid #90caf9" }}>
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                {BOARD_TYPE_LABELS[boardType]}
                {boardType === "wallpaper" ? ` (${wallpaperInstall ? "مع تركيب" : "بدون تركيب"})` : ""}
              </Typography>
              <Typography variant="h5" fontWeight={900} color="primary.main">
                {fmtIQD(simplePriceInfo.total)}
              </Typography>
              {boardType !== "wallpaper" && (
                <Typography variant="caption" color="warning.dark" display="block" mt={1}>
                  ⚠️ سعر مبدئي مؤقت لحد ما تتحدد الأسعار والقياسات النهائية لهذا النوع
                </Typography>
              )}
              {isAdmin && (
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  المساحة: {simplePriceInfo.areaM2.toFixed(2)} م² × {fmtIQD(simplePriceInfo.unitPrice)} / م²
                </Typography>
              )}
            </Box>
          ) : (
            <Typography sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: 2 }}>ادخلي الطول والعرض لإظهار النتائج</Typography>
          )
        )}

        {boardType === "canvas" && (results ? (
          // التعديل: إذا كان القياس ستاندرد، تظهر رسالة ويختفي الجدول التلقائي
          isStandard ? (
            <Box sx={{ p: 3, textAlign: 'center', backgroundColor: '#e8f5e9', borderRadius: 2, border: '1px solid #c8e6c9' }}>
              <Typography variant="body1" fontWeight={700} color="success.main">
                ✨ هذا القياس متوفر في قائمة الأسعار المرجعية أدناه.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead sx={{ backgroundColor: "#fafafa" }}>
                  <TableRow>
                    <TableCell align="right" sx={{ fontWeight: 900 }}>النوع</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 900 }}>Origin</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 900 }}>Edition</TableCell>
                    {!isEmployee && <TableCell align="center" sx={{ fontWeight: 900 }}>Prime</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell align="right" sx={{ fontSize: '0.85rem' }}>{r.title}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#2e7d32' }}>{fmtIQD(r.origin)}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#1976d2' }}>{fmtIQD(r.edition)}</TableCell>
                      {!isEmployee && <TableCell align="center" sx={{ fontWeight: 700, color: '#d32f2f' }}>{fmtIQD(r.prime)}</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : (
          <Typography sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: 2 }}>{enterDimsText}</Typography>
        ))}

        {/* تفاصيل الإدارة - تظهر فقط للأدمن وفقط إذا لم يكن القياس ستاندرد */}
      {/* تفاصيل الإدارة - تظهر فقط للأدمن سواء القياس ستاندرد أو غير ستاندرد */}
{isAdmin && boardType === "canvas" && results && (
  <Box
    sx={{
      mt: 3,
      p: 2,
      backgroundColor: "#fffde7",
      borderRadius: 2,
      border: "1px solid #ffd54f",
    }}
  >
    <Typography variant="subtitle2" fontWeight={900} mb={1}>
      🛡️ تفاصيل الكلف (للإدارة فقط):
    </Typography>

    <Grid container spacing={1} sx={{ fontSize: "0.8rem" }}>
      <Grid item xs={6}>
        طباعة: {fmtIQD(results.debug.printCost)}
      </Grid>

      <Grid item xs={6}>
        خشب داخلي: {fmtIQD(results.debug.innerWood)}
      </Grid>

      <Grid item xs={6}>
        إطار أيل: {fmtIQD(results.debug.ail)}
      </Grid>

      <Grid item xs={6}>
        إطار كاس: {fmtIQD(results.debug.kas)}
      </Grid>

      <Grid item xs={6}>
        إطار إيل: {fmtIQD(results.debug.printCost + results.debug.innerWood + results.debug.ail)}
      </Grid>

      <Grid item xs={6}>
        إطار كاس: {fmtIQD(results.debug.printCost + results.debug.innerWood + results.debug.kas)}
      </Grid>
    </Grid>
  </Box>
)}
      </Paper>

      {/* القائمة المرجعية تظهر بنسخة ارت تاتش هم (بدون "أقصى قياس") */}
      {boardType === "canvas" && (
        <>
          <hr style={{ opacity: 0.2, marginBottom: "30px" }} />

          {/* 2️⃣ قائمة الأسعار السفلية */}
          <Typography variant="h5" fontWeight={900} mb={3} align="center">💰 قائمة الأسعار المرجعية</Typography>

          {/* أكبر قياسات - مخفية بنسخة ارت تاتش */}
          {!partnerMode && (
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6}>
              <Paper sx={{ p: 2, textAlign: "center", backgroundColor: "#e3f2fd" }}>
                <Typography variant="caption" fontWeight={800}>🪵 خشب / فوم (أقصى قياس)</Typography>
                <Typography variant="body2" fontWeight={700}>160 × 240 سم</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper sx={{ p: 2, textAlign: "center", backgroundColor: "#fce4ec" }}>
                <Typography variant="caption" fontWeight={800}>🎨 كانفاس (أقصى قياس)</Typography>
                <Typography variant="body2" fontWeight={700}>160 × 240 سم</Typography>
              </Paper>
            </Grid>
          </Grid>
          )}

          {/* الجداول */}
          <PriceTable title="🖼️ Frame (أيل)" data={frameData} width={lengthCm} height={widthCm} />
          <PriceTable title="🎨 Kas Frame (كاس)" data={kasData} width={lengthCm} height={widthCm} />
        </>
      )}
    </Container>
  );
}